import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Table,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import OrderLineItemDetails from '../../components/Orders/OrderLineItemDetails';
import { GET_ORDER, GetOrderData, GetOrderVariables } from '../../graphql/queries/orders';
import { formatOrderTotal } from '../../utils/orderLineItemMeta';
import { useInvoiceNumberMap } from '../../hooks/useInvoiceNumberMap';
import BookingStatusSelect from '../../components/Bookings/BookingStatusSelect';
import CustomerCancelRequestButton from '../../components/Orders/CustomerCancelRequestButton';
import AccessDenied from '../Utility/AccessDenied';
import { useOwnOrderAccess } from '../../hooks/useOwnOrderAccess';

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { invoiceNumberMap, ready: invoiceMapReady } = useInvoiceNumberMap();

  const { data, loading, error } = useQuery<GetOrderData, GetOrderVariables>(GET_ORDER, {
    variables: { id: id || '', idType: 'DATABASE_ID' },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const order = data?.order;
  const { denied } = useOwnOrderAccess(order, { loading: loading && !order, error });

  const orderNumberLabel = order
    ? `Order #${order.orderNumber ?? order.databaseId}`
    : id
      ? `Order #${id}`
      : 'Order';

  const invoiceNumber = order ? invoiceNumberMap.get(order.databaseId) : undefined;
  const invoiceLabel = invoiceNumber ? `Invoice ${invoiceNumber}` : undefined;

  document.title = invoiceLabel
    ? `${invoiceLabel} | ${orderNumberLabel} | NGK Tours`
    : `${orderNumberLabel} | NGK Tours`;

  if (!id) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Order" breadcrumbItem="Invoice Details" />
          <Alert color="warning">Order ID is missing.</Alert>
          <Link to="/orders-list">Back to Orders</Link>
        </Container>
      </div>
    );
  }

  if (denied) {
    return <AccessDenied />;
  }

  if (loading && !order) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Order" breadcrumbItem="Invoice Details" />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading order...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Order" breadcrumbItem="Invoice Details" />
          <Alert color="danger">{(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]?.message || error.message}</Alert>
          <Link to="/orders-list">Back to Orders</Link>
        </Container>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Order" breadcrumbItem="Invoice Details" />
          <Alert color="info">Order not found.</Alert>
          <Link to="/orders-list">Back to Orders</Link>
        </Container>
      </div>
    );
  }

  const customerName =
    order.billing?.firstName || order.billing?.lastName
      ? [order.billing.firstName, order.billing.lastName].filter(Boolean).join(' ')
      : order.billing?.email || '—';

  const lineItems = order.lineItems?.nodes ?? [];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={invoiceLabel ?? orderNumberLabel}
          breadcrumbItem="Invoice Details"
        />
        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                <h5 className="mb-3">
                  {invoiceLabel ? (
                    <>
                      {invoiceLabel}
                      <span className="text-muted mx-2">·</span>
                      <span className="text-muted">{orderNumberLabel}</span>
                    </>
                  ) : !invoiceMapReady ? (
                    <>
                      <span className="text-muted">Invoice …</span>
                      <span className="text-muted mx-2">·</span>
                      {orderNumberLabel}
                    </>
                  ) : (
                    orderNumberLabel
                  )}
                </h5>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Status</Col>
                  <Col sm={8}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <BookingStatusSelect
                        orderId={order.databaseId}
                        status={order.status}
                        compact={false}
                      />
                      <CustomerCancelRequestButton
                        orderId={order.databaseId}
                        status={order.status}
                        size="sm"
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Date</Col>
                  <Col sm={8}>{formatDate(order.date)}</Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Customer</Col>
                  <Col sm={8}>{customerName} {order.billing?.email && `(${order.billing.email})`}</Col>
                </Row>
                <Row className="mb-2">
                  <Col sm={4} className="text-muted">Payment</Col>
                  <Col sm={8}>{order.paymentMethodTitle || '—'}</Col>
                </Row>

                <hr />
                <h6 className="mb-3">Order items</h6>
                {lineItems.length === 0 ? (
                  <p className="text-muted mb-0">No line items.</p>
                ) : (
                  lineItems.map((item, index) => (
                    <div
                      key={`${item.productId ?? 'item'}-${index}`}
                      className={`border rounded p-3 ${index < lineItems.length - 1 ? 'mb-3' : ''}`}
                    >
                      <OrderLineItemDetails
                        productName={item.product?.node?.name || 'Product'}
                        meta={item.metaData}
                        quantity={item.quantity}
                        total={item.total ?? undefined}
                        currency={order.currency}
                      />
                    </div>
                  ))
                )}

                <Row className="mt-3">
                  <Col sm={{ size: 6, offset: 6 }}>
                    <Table size="sm" className="mb-0">
                      <tbody>
                        {order.shippingLines?.nodes?.length ? (
                          order.shippingLines.nodes.map((s, index) => (
                            <tr key={`${s.methodTitle ?? 'ship'}-${index}`}>
                              <td className="text-muted">Shipping</td>
                              <td>
                                {s.methodTitle}{' '}
                                {formatOrderTotal(s.total, order.currency)}
                              </td>
                            </tr>
                          ))
                        ) : null}
                        <tr>
                          <td className="text-muted">Total</td>
                          <td>
                            <strong>{formatOrderTotal(order.total, order.currency)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col lg={4}>
            <Card>
              <CardBody>
                <h6 className="mb-3">Billing</h6>
                <p className="mb-1">
                  {[order.billing?.firstName, order.billing?.lastName].filter(Boolean).join(' ')}
                </p>
                <p className="mb-1 text-muted small">{order.billing?.email}</p>
                <p className="mb-1 text-muted small">{order.billing?.phone}</p>
                <p className="mb-0 text-muted small">
                  {[order.billing?.address1, order.billing?.address2, order.billing?.city, order.billing?.state, order.billing?.postcode, order.billing?.country]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </CardBody>
            </Card>
            {order.shipping && (order.shipping.address1 || order.shipping.city) && (
              <Card className="mt-3">
                <CardBody>
                  <h6 className="mb-3">Shipping</h6>
                  <p className="mb-0 text-muted small">
                    {[order.shipping.firstName, order.shipping.lastName].filter(Boolean).join(' ')}
                    <br />
                    {[order.shipping.address1, order.shipping.address2, order.shipping.city, order.shipping.state, order.shipping.postcode, order.shipping.country]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
        <div className="mt-3">
          <Link to="/orders-list" className="btn btn-secondary">Back to Orders</Link>
        </div>
      </Container>
    </div>
  );
};

export default OrderDetails;
